import React, { useState } from "react";
import moment from "moment";
import { withGeneral } from "../../context/mixin/with-general";
import GoBack from "../../components/go-back";
import InputBox from "../../components/input-box/input-box";
import SelectBox from "../../components/select-box/select-box";
import MainButton from "../../components/main-button";
import { MainTableContainer } from "../../components/main-table/main-table-style";
import BodyText from "../../components/components-text/body-text";
import LeftInfoModal from "../../components/left-info-modal";
import DicotomicCheckbox from "../../components/dicotomic-checkbox/dicotomic-checkbox";
import { PrivilegesContainer } from "./privileges-style";
import { PrivilegeCode, IUserPrivilegeItem } from "../../services/PrivilegesServices-dto";
import PrivilegesServices from "../../services/PrivilegesServices";
import MediatorServices from "../../services/MediatorServices";
import { images } from "../../assets";
import { formatPrivilegesList, privilegesTableColumns } from "./utils";
import { PrivilegesProps, Filter, Error } from "./privileges-dto";
import usePrivilegesList from "./hooks/usePrivilegesList";
import PrivilegesError from "./privileges-error";
import PrivilegesUpload from "./privileges-upload";

const Privileges: React.FC<PrivilegesProps> = ({ setLoading }) => {
  const [filter, setFilter] = useState<Filter>({ user: "", privileges: [] });
  const [userPrivilegesList, setUserPrivilegesList] = useState<IUserPrivilegeItem[]>([]);
  const [userExcludedPrivileges, setUserExcludedPrivileges] = useState<string[]>([]);
  const [showTable, setShowTable] = useState<Boolean>(false);
  const [error, setError] = useState<Error>({ showError: false, mainTitle: "", mainText: "" });
  const [isSaved, setIsSaved] = useState<boolean>(false);

  const { privilegesOptions, privilegesListError, setPrivilegesListError } = usePrivilegesList({
    setLoading,
  });

  const handleChangeUser = (value: string) => {

    setFilter((prev) => ({ ...prev, user: value }));
    setShowTable(false);
  };
  const handleBlurUser = (value: string) => {
    const val = value.replace(/ +/g, '');
    handleChangeUser(val)
  }

  const handleChangeStatus = (privilege: string, value: boolean) => {
    setUserExcludedPrivileges((prev) =>
      value ? [...prev, privilege] : prev.filter((item) => item !== privilege)
    );
  };

  const handleUpdateUser = async () => {
    const response = await PrivilegesServices.postUserPrivileges({
      setLoading,
      loader: true,
      data: {
        user: filter.user,
        privileges: userExcludedPrivileges as PrivilegeCode[],
      },
    });
    if ("ok" in response) {
      const newUserPrivilegesList = formatPrivilegesList(response.data).sort((a, b) => a.code > b.code ? 1 : -1);
      setUserPrivilegesList(newUserPrivilegesList);
      setIsSaved(true);
    } else {
      setError({
        showError: true,
        mainTitle: `Privilegios del usuario ${filter.user} no actualizados`,
        mainText: ("message" in response && response.message) || "Error inesperado",
      });
    }
  };

  const handleClickSearch = async () => {
    const isSendaUser = await getSendaUser();
    if (!isSendaUser) return;

    const response = await PrivilegesServices.getUserPrivileges({
      setLoading,
      loader: true,
      user: filter.user,
    });

    if ("ok" in response && response.ok) {
      const userPrivileges = response.data.privileges.sort((a, b) => a.code > b.code ? 1 : -1);
      setUserPrivilegesList(userPrivileges);
      const newUserExcludedPrivileges: string[] = [];
      userPrivileges.forEach((privilege: IUserPrivilegeItem) => {
        if (privilege.enabled) newUserExcludedPrivileges.push(privilege.code);
      });
      setUserExcludedPrivileges(newUserExcludedPrivileges);
      setShowTable(true);
    } else {
      setError({
        showError: true,
        mainTitle: `Usuario ${filter.user} no encontrado`,
        mainText: ("message" in response && response.message) || "Error inesperado",
      });
    }
  };

  const getSendaUser = async () => {
    const sendaUser = await MediatorServices.getSendaUser({
      setLoading,
      loader: true,
      user: filter.user,
    });

    if ("ok" in sendaUser && sendaUser.ok) return true;

    setError({
      showError: true,
      mainTitle: "El usuario no existe en BBDD",
      mainText: "No se pueden actualizar los privilegios porque no existe el usuario en BBDD.",
    });

    return false;
  };

  return (
    <PrivilegesContainer>
      <div className="black-arrow-container">
        <GoBack text="Volver" />
      </div>

      <h2>GESTIÓN DE PRIVILEGIOS</h2>
      <h3>Filtrar</h3>

      <div className="filter-container">
        <div className="inputBox filter-input">
          <InputBox
            required
            onChange={(value: string) => handleChangeUser(value)}
            onBlur={(value: string) => handleBlurUser(value)}
            placeholder="Usuario"
            type="text"
            value={filter.user}
          />
        </div>
        <div className="inputBox filter-select">
          <SelectBox
            selectedMultipleOptions={filter.privileges}
            multiple
            placeholder="Todos los privilegios"
            optionsText={privilegesOptions}
            optionKey="key"
            optionValue="text"
            onMultipleChange={(values: string[]) => setFilter({ ...filter, privileges: values })}
            onChange={() => {}}
          />
        </div>
        <div className="filter-search">
          <MainButton
            icon
            iconImg={filter.user ? images.IconSearchWhite : images.IconSearchGray}
            disabled={!filter.user}
            text="Buscar"
            onClick={handleClickSearch}
          />
        </div>
      </div>

      {showTable && (
        <>
          <div className="table-limit">
            <MainTableContainer className="table noZebra">
              <thead>
                <tr>
                  {privilegesTableColumns.map((column: string) => (
                    <th key={column}>
                      <BodyText className="dimmed-mini-title">
                        <p>{column}</p>
                      </BodyText>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {userPrivilegesList.map((privilege: IUserPrivilegeItem) => {
                  const { code, name, updatedAt } = privilege;
                  const updated = moment(updatedAt).format("DD/MM/YYYY");

                  if (filter.privileges.length > 0 && !filter.privileges.includes(privilege.code))
                    return null;

                  return (
                    <tr className="main-table-row" key={code}>
                      <td>
                        <BodyText className="dimmed-mini-body">
                          <p>{filter.user}</p>
                        </BodyText>
                      </td>
                      <td>
                        <BodyText className="dimmed-mini-body">
                          <p>{code}</p>
                        </BodyText>
                      </td>
                      <td>
                        <BodyText className="dimmed-mini-body">
                          <p>{name}</p>
                        </BodyText>
                      </td>
                      <td>
                        <BodyText className="dimmed-mini-body">
                          <p>{updated}</p>
                        </BodyText>
                      </td>
                      <td>
                        <BodyText className="dimmed-mini-body">
                          <div className="inputBox">
                            <DicotomicCheckbox
                              className="optional"
                              checkboxText=""
                              onChange={(value: boolean) => handleChangeStatus(code, value)}
                              initChecked={userExcludedPrivileges.includes(code)}
                            />
                          </div>
                        </BodyText>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </MainTableContainer>
          </div>

          <div className="save-row">
            <div className="save-text">
              <p>*Para actualizar los datos es necesario guardar</p>
            </div>

            <div className="save-button">
              <MainButton text="Guardar" onClick={handleUpdateUser} />
            </div>
          </div>
        </>
      )}

      {isSaved && (
        <LeftInfoModal
          active={isSaved}
          className="info"
          mainTitle={`Usuario ${filter.user} actualizado`}
          mainText="Se han modificado los privilegios del usuario."
          buttonText="Cerrar"
          img={images.IconInfoBlue}
          close={() => setIsSaved(false)}
          onClickAccept={() => setIsSaved(false)}
          noSecondaryButton
        />
      )}
      {error.showError && <PrivilegesError error={error} setError={setError} />}
      {privilegesListError.showError && (
        <PrivilegesError error={privilegesListError} setError={setPrivilegesListError} />
      )}

      <PrivilegesUpload />
    </PrivilegesContainer>
  );
};

export default withGeneral(Privileges);
